import { CalendarDays, Check, Download, FileText, FlaskConical, MessageCircle, Syringe, Upload } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { waLink, patientToProMessage } from "@/lib/whatsapp";

const TIMELINE = [
  { key: "submitted", label: "Enviada a tu médico" },
  { key: "payment_verified", label: "Pago verificado" },
  { key: "in_review", label: "En revisión" },
  { key: "completed", label: "Receta lista" },
];

const REMINDER_ICONS = { aplicacion: Syringe, laboratorios: FlaskConical, consulta: CalendarDays };
const REMINDER_LABELS = {
  aplicacion: "Aplicación de medicamento",
  laboratorios: "Toma de laboratorios",
  consulta: "Consulta de seguimiento",
};

const FILE_KIND_LABELS = { lab: "Laboratorios", receta_anterior: "Receta previa", payment_proof: "Comprobante de pago" };

function stepIndex(status) {
  if (status === "submitted") return 1;
  if (status === "in_review") return 3;
  if (status === "completed") return 4;
  return 0;
}

export function PaymentRejectedBanner({ req }) {
  if (req.status !== "payment_rejected") return null;
  const personal = req.personal || {};
  const wa = waLink(
    req.professional_whatsapp,
    patientToProMessage({
      patientName: `${personal.nombres || ""} ${personal.apellidos || ""}`.trim(),
      folio: req.folio || "",
    }),
  );
  return (
    <div className="border-2 border-red-300 rounded-2xl bg-red-50 p-6 mb-6" data-testid="payment-rejected-banner">
      <h2 className="font-heading font-semibold text-lg text-red-800 mb-1">
        Tu comprobante de pago fue rechazado
      </h2>
      <p className="text-sm text-red-700 mb-4">
        {req.payment_rejection_reason
          ? `Motivo: ${req.payment_rejection_reason}`
          : "Tu especialista no pudo validar el comprobante."}{" "}
        Comunícate con tu especialista por WhatsApp para aclararlo y sube un nuevo comprobante.
      </p>
      <div className="flex flex-wrap gap-3">
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-pro-btn-rejected">
            <Button className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white">
              <MessageCircle className="h-4 w-4 mr-2" strokeWidth={1.5} /> WhatsApp de tu especialista
            </Button>
          </a>
        )}
        <Link to={`/nueva-solicitud?continuar=${req.id}`} data-testid="reupload-proof-link">
          <Button variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-100">
            <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} /> Subir nuevo comprobante
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function PaymentPendingBanner({ req }) {
  if (req.status !== "submitted") return null;
  return (
    <div className="border border-amber-300 rounded-2xl bg-amber-50 p-5 mb-6 text-sm text-amber-900" data-testid="payment-pending-banner">
      Tu especialista está verificando tu comprobante de pago. Te notificaremos en cuanto tu
      solicitud pase a revisión.
    </div>
  );
}

export function RequestTimeline({ status }) {
  const idx = stepIndex(status);
  return (
    <div className="border rounded-2xl bg-white p-6 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {TIMELINE.map((t, i) => {
          const done = idx > i;
          return (
            <div key={t.key} className="flex sm:flex-col items-center sm:items-start gap-3">
              <span
                className={`h-7 w-7 rounded-full flex items-center justify-center ${
                  done ? "bg-primary text-white" : "bg-secondary text-muted-foreground"
                }`}
              >
                {done ? <Check className="h-4 w-4" strokeWidth={2} /> : <span className="text-xs font-bold">{i + 1}</span>}
              </span>
              <p className={`text-sm ${done ? "font-semibold" : "text-muted-foreground"}`}>{t.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RequestInfoCard({ req }) {
  const personal = req.personal || {};
  const wa = waLink(
    req.professional_whatsapp,
    patientToProMessage({
      patientName: `${personal.nombres || ""} ${personal.apellidos || ""}`.trim(),
      folio: req.folio || "",
    }),
  );
  return (
    <div className="border rounded-2xl bg-white p-6 mb-6 text-sm space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p>
          <span className="font-semibold">Médico:</span> {req.professional_name}
        </p>
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-pro-btn">
            <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white">
              <MessageCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> WhatsApp
            </Button>
          </a>
        )}
      </div>
      <p>
        <span className="font-semibold">Paciente:</span> {req.personal?.nombres} {req.personal?.apellidos} ({req.personal?.edad} años)
      </p>
      <div>
        <span className="font-semibold">Documentos enviados:</span>
        <ul className="mt-2 space-y-1">
          {req.files?.map((f) => (
            <li key={f.file_id} className="flex items-center gap-2 text-muted-foreground">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
              <a
                href={`${API}/files/${f.file_id}`}
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary hover:underline"
                data-testid={`patient-file-link-${f.file_id}`}
              >
                {FILE_KIND_LABELS[f.kind] || "Documento"} · {f.original_filename}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function RemindersSection({ req, reminders }) {
  if (req.status !== "completed" || reminders.length === 0) return null;
  return (
    <div className="border rounded-2xl bg-white p-6 mb-6" data-testid="reminders-section">
      <h2 className="font-heading font-semibold text-lg mb-1">Próximas fechas</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Te enviaremos un recordatorio por correo un día antes de cada fecha.
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        {reminders.map((r) => {
          const Icon = REMINDER_ICONS[r.type] || CalendarDays;
          const label = REMINDER_LABELS[r.type] || "Recordatorio";
          return (
            <div key={r.id} className="flex items-center gap-3 rounded-xl bg-secondary px-4 py-3" data-testid={`reminder-${r.id}`}>
              <Icon className="h-4 w-4 text-primary shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(r.due_date + "T12:00:00").toLocaleDateString("es-MX", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                  {r.sent ? " · recordatorio enviado" : ""}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FinalPrescriptionSection({ req }) {
  if (req.status !== "completed") return null;
  return (
    <div className="border-2 border-primary/30 rounded-2xl bg-accent/30 p-6" data-testid="final-prescription-section">
      <h2 className="font-heading font-semibold text-lg mb-2">Tu receta está lista 🎉</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Descárgala en PDF. Incluye tu receta médica y la nota de evolución.
      </p>
      <a href={`${API}/requests/${req.id}/pdf`} target="_blank" rel="noreferrer">
        <Button className="rounded-full" data-testid="download-pdf-btn">
          <Download className="h-4 w-4 mr-2" strokeWidth={1.5} /> Descargar receta (PDF)
        </Button>
      </a>
      {req.final?.dx && (
        <div className="mt-6 bg-white border rounded-xl p-5 text-sm space-y-4">
          <p>
            <span className="font-semibold">Dx:</span> {req.final.dx}
          </p>
          <div>
            <p className="font-semibold mb-1">Medicamentos / Indicaciones:</p>
            <p className="whitespace-pre-wrap text-muted-foreground">{req.final.receta}</p>
          </div>
        </div>
      )}
    </div>
  );
}
